import { cleanFloat } from "@/lib/tool-kit/grades/rounding";

/**
 * Dutch 1–10 grades ↔ US letters/GPA and UK classes.
 *
 * Sources (retrieved October 2026):
 * - Nuffic (the Dutch organisation for internationalisation in education and the Dutch
 *   ENIC-NARIC centre), "Grading systems in the Netherlands, the United States and the United
 *   Kingdom", July 2009 (hosted by Utrecht University,
 *   https://students.uu.nl/sites/default/files/geo-grading-systems-holland-vs-us-uk.pdf). Its
 *   conversion table, which it says "reflects the opinion of Nuffic" and is based on the data
 *   available to Nuffic in 2006 and 2009, is the table below, row for row:
 *     NL   US     UK      class
 *     10   A+     A+      first
 *     9.5  A+     A+      first
 *     9    A+     A+      first
 *     8.5  A+     A       first
 *     8    A      A/A−    first
 *     7.5  A/A−   B+      upper second
 *     7    B+     B       upper second
 *     6.5  B      C+      lower second
 *     6    B−/C   C/D     lower second
 *     5.5  D      D       third
 *     5–1  F      F       –
 *   Erasmus University Rotterdam (ESSB, "Grading system", https://www.eur.nl/en/essb/grading)
 *   republishes the US column, credited to Nuffic, with the same letters (10 and 9 A+, 8 A, 7 B+,
 *   6 B−/C, 5.5 D, below 5.5 F) and the same warning against equating 90–100% with a Dutch 10.
 *   Nuffic's document is copyrighted by Nuffic; it is used here as a cited reference scale,
 *   with the rows as data, not reproduced as prose.
 * - The scale and the pass mark: the same Nuffic document says the traditional scale runs from
 *   1 to 10, that grades 1 to 3 and 9 and 10 are very rarely given, and that 6 and 7 are the
 *   most common grades in secondary and higher education. EUR states "The passing mark for a
 *   single subject is 5.5", and Nuffic's grading page (https://www.nuffic.nl/en/node/65) says 6
 *   is the minimum pass mark in secondary education, where a 5.5 is rounded to 6, and that in
 *   higher education a 5.5 is sometimes also sufficient.
 *
 * Where Nuffic gives two letters (A/A−, B−/C), the reader chooses whether to take the upper, the
 * lower or the midpoint of the pair for grade points. A grade between two table rows takes the
 * row below it (7.3 reads as the 7 row) — an assumption, because the table steps in halves.
 * Grade points are the common US values: A+ and A 4.0, A− 3.7, B+ 3.3, B 3.0, B− 2.7, C+ 2.3,
 * C 2.0, D 1.0, F 0.0 (A+ is not scored above 4.0).
 */

export type PairRule = "upper" | "middle" | "lower";
export const PAIR_RULES: readonly PairRule[] = ["upper", "middle", "lower"];

export type UkClass =
  | "first"
  | "upper-second"
  | "lower-second"
  | "third"
  | "fail";

const POINTS: Record<string, number> = {
  "A+": 4,
  A: 4,
  "A−": 3.7,
  "B+": 3.3,
  B: 3,
  "B−": 2.7,
  "C+": 2.3,
  C: 2,
  D: 1,
  F: 0,
};

export interface DutchRow {
  /** The lowest grade, inclusive, that takes this row. */
  min: number;
  /** One letter, or two when Nuffic gives a pair, best first. */
  us: readonly string[];
  uk: readonly string[];
  ukClass: UkClass;
}

export const DUTCH_ROWS: readonly DutchRow[] = [
  { min: 10, us: ["A+"], uk: ["A+"], ukClass: "first" },
  { min: 9.5, us: ["A+"], uk: ["A+"], ukClass: "first" },
  { min: 9, us: ["A+"], uk: ["A+"], ukClass: "first" },
  { min: 8.5, us: ["A+"], uk: ["A"], ukClass: "first" },
  { min: 8, us: ["A"], uk: ["A", "A−"], ukClass: "first" },
  { min: 7.5, us: ["A", "A−"], uk: ["B+"], ukClass: "upper-second" },
  { min: 7, us: ["B+"], uk: ["B"], ukClass: "upper-second" },
  { min: 6.5, us: ["B"], uk: ["C+"], ukClass: "lower-second" },
  { min: 6, us: ["B−", "C"], uk: ["C", "D"], ukClass: "lower-second" },
  { min: 5.5, us: ["D"], uk: ["D"], ukClass: "third" },
  { min: 1, us: ["F"], uk: ["F"], ukClass: "fail" },
];

export const DUTCH_MIN_GRADE = 1;
export const DUTCH_MAX_GRADE = 10;
/** Pass mark for a single subject at most universities (EUR; Nuffic: sometimes sufficient). */
export const DUTCH_PASS_MARK = 5.5;

export function isValidDutchGrade(grade: number): boolean {
  return (
    Number.isFinite(grade) &&
    grade >= DUTCH_MIN_GRADE &&
    grade <= DUTCH_MAX_GRADE
  );
}

export function dutchRow(grade: number): DutchRow | null {
  if (!isValidDutchGrade(grade)) {
    return null;
  }
  return DUTCH_ROWS.find((row) => grade >= row.min) ?? null;
}

/** Grade points for a row's US letter(s) under the chosen rule for a pair. */
export function dutchPoints(row: DutchRow, rule: PairRule): number {
  const points = row.us.map((letter) => POINTS[letter] ?? 0);
  const best = Math.max(...points);
  const worst = Math.min(...points);
  if (rule === "upper") {
    return best;
  }
  return rule === "lower" ? worst : cleanFloat((best + worst) / 2);
}

export interface DutchCourse {
  grade: number;
  credits: number;
}

export interface DutchSummary {
  average: number;
  gpa: number;
  row: DutchRow;
  totalCredits: number;
  belowPass: number;
}

export function summariseDutch(
  courses: readonly DutchCourse[],
  rule: PairRule
): DutchSummary | null {
  let credits = 0;
  let gradeSum = 0;
  let pointSum = 0;
  let belowPass = 0;
  for (const course of courses) {
    const row = dutchRow(course.grade);
    if (row && course.credits > 0) {
      credits += course.credits;
      gradeSum += course.grade * course.credits;
      pointSum += dutchPoints(row, rule) * course.credits;
      if (course.grade < DUTCH_PASS_MARK) {
        belowPass += 1;
      }
    }
  }
  if (credits <= 0) {
    return null;
  }
  const average = cleanFloat(gradeSum / credits);
  const row = dutchRow(average);
  if (row === null) {
    return null;
  }
  return {
    average,
    gpa: cleanFloat(pointSum / credits),
    row,
    totalCredits: credits,
    belowPass,
  };
}
